/**
 * @fileoverview CAP.IO Home Page (Dashboard)
 * Main landing page with hero, live stress board, and dispatches.
 * Observatory Network cards are on the /observatories page.
 * 
 * @module pages/home
 */

import { el } from "../shared/components/simpleRender.js";
import { 
  preloadAllSignals, 
  getLevelColor, 
  SIGNAL_METADATA,
  CATEGORIES 
} from "../api/capitulators.js";

// Featured signals to show on the home page stress board
// Curated selection of key indicators across categories
const FEATURED_SIGNALS = [
  'NET_LIQUIDITY',      // Liquidity - our flagship signal
  'VIXCLS',             // Volatility - fear gauge
  'CURVE_2S10S',        // Rates - yield curve
  'BAMLH0A0HYM2',       // Credit - HY spreads
  'NFCI',               // Macro - financial conditions
  'DTWEXBGS',           // FX - dollar strength
  'SOFR',               // Funding - money market
  'ICSA',               // Labor - jobless claims
  'DCOILWTICO',         // Commodities - oil
  'T10YIE',             // Inflation - breakevens
  'DGS10',              // Rates - 10Y Treasury
  'STLFSI4',            // Macro - financial stress
];

/**
 * Formats a signal value with appropriate precision and units
 * @param {number|null} value 
 * @param {string} signalId 
 * @returns {string}
 */
function formatSignalValue(value, signalId) {
  if (value == null) return '—';
  
  const metadata = SIGNAL_METADATA[signalId] || {};
  const units = metadata.units || '';
  
  // Large numbers (billions/millions)
  if (signalId === 'NET_LIQUIDITY' || units === 'B') {
    // Value is likely in billions
    if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(2)}T`;
    }
    return `$${value.toFixed(0)}B`;
  }
  
  if (units === 'M') {
    if (Math.abs(value) >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}T`;
    }
    if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(1)}B`;
    }
    return `$${value.toFixed(0)}M`;
  }
  
  if (units === 'K') {
    return `${value.toFixed(0)}K`;
  }
  
  // Percentages and basis points
  if (units === '%') {
    return `${value.toFixed(2)}%`;
  }
  
  if (units === 'bps') {
    return `${value.toFixed(0)} bps`;
  }
  
  if (units === 'σ') {
    return `${value.toFixed(2)}σ`;
  }
  
  // Currency units
  if (units === '$/bbl' || units === '$/MMBtu') {
    return `$${value.toFixed(2)}`;
  }
  
  // Default formatting
  if (Math.abs(value) >= 1000) {
    return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  if (Math.abs(value) < 0.01) {
    return value.toFixed(4);
  }
  if (Math.abs(value) < 10) {
    return value.toFixed(2);
  }
  return value.toFixed(1);
}

/**
 * Creates a live stress card with real signal data
 * @param {Object} signal - Signal data from API
 * @returns {HTMLElement}
 */
function createLiveStressCard(signal) {
  const { signal_id, name, category, current } = signal;
  const value = current?.value;
  const level = current?.level || 'normal';
  const levelColor = getLevelColor(level);
  const categoryInfo = CATEGORIES[category] || {};
  
  // Format the level label for display
  const levelLabel = level.charAt(0).toUpperCase() + level.slice(1);
  
  // Determine label type based on level
  let labelType = 'neutral';
  if (['high', 'stressed', 'extreme', 'crisis', 'inverted', 'wide'].includes(level)) {
    labelType = 'danger';
  } else if (['elevated', 'flat'].includes(level)) {
    labelType = 'warning';
  }
  
  const card = el("div", { 
    className: "widget-card stress-card stress-card--live",
    onclick: () => { window.location.hash = '#/signals'; }
  }, [
    el("div", { className: "stress-card__category" }, [
      el("span", { className: "stress-card__category-icon" }, [categoryInfo.icon || '📊']),
      el("span", { className: "stress-card__category-name" }, [categoryInfo.name || category])
    ]),
    el("div", { className: "stress-card__name" }, [name]),
    el("div", { 
      className: "stress-card__value",
      style: { color: levelColor }
    }, [formatSignalValue(value, signal_id)]),
    el("span", { className: `stress-label label-${labelType}` }, [levelLabel])
  ]);
  
  return card;
}

/**
 * Creates a loading skeleton for stress cards
 * @returns {HTMLElement}
 */
function createStressCardSkeleton() {
  return el("div", { className: "widget-card stress-card stress-card--skeleton" }, [
    el("div", { className: "skeleton skeleton-category" }, []),
    el("div", { className: "skeleton skeleton-name" }, []),
    el("div", { className: "skeleton skeleton-value" }, []),
    el("div", { className: "skeleton skeleton-label" }, [])
  ]);
}

/**
 * Renders the main home page
 * @returns {HTMLElement}
 */
export function renderHomePage() {
  return el("div", { className: "home-page" }, [
    // Hero Section
    el("section", { className: "section hero-section" }, [
      el("div", { className: "hero-grid" }, [
        el("div", { className: "hero-content" }, [
          el("h1", { className: "hero-headline" }, [
            "A real-time macro terminal for systemic market risk."
          ]),
          el("p", { className: "hero-subline" }, [
            "A gothic macro terminal for people who want to see capitulation coming, not read about it after the close."
          ]),
          el("div", { className: "hero-buttons" }, [
            el("a", { href: "#/dispatches", className: "btn-primary" }, [
              "Enter the Dispatches"
            ]),
            el("a", { href: "#/manifesto", className: "btn-ghost" }, [
              "Read the Manifesto"
            ])
          ])
        ]),
        el("div", { className: "hero-orb-container" }, [
          el("img", { 
            src: "/assets/images/The_Watcher_Cropped.png",
            alt: "The Watcher - The Silent Witness of the System",
            className: "hero-watcher-image"
          }, [])
        ])
      ])
    ]),

    // Station Updates - News/Changelog Section
    el("section", { className: "section station-updates-section" }, [
      el("div", { className: "station-updates" }, [
        el("div", { className: "station-updates__header" }, [
          el("span", { className: "station-updates__icon" }, ["📡"]),
          el("span", { className: "station-updates__title" }, ["Station Updates"]),
          el("span", { className: "station-updates__badge" }, ["NEW"])
        ]),
        el("div", { className: "station-updates__content" }, [
          el("div", { className: "station-update station-update--featured" }, [
            el("div", { className: "station-update__meta" }, [
              el("span", { className: "station-update__date" }, ["Dec 22, 2024"]),
              el("span", { className: "station-update__tag station-update__tag--live" }, ["🟢 LIVE"])
            ]),
            el("h3", { className: "station-update__title" }, [
              "Signals Page Now Live for Alpha Testing"
            ]),
            el("p", { className: "station-update__description" }, [
              "The full Signals terminal is now operational. Track 120+ macro signals across 9 categories with our tiered intelligence system — from Flagship composites to raw FRED source data."
            ]),
            el("div", { className: "station-update__features" }, [
              el("span", { className: "station-update__feature" }, ["📊 Interactive Heatmap"]),
              el("span", { className: "station-update__feature" }, ["🔍 Signal Documentation"]),
              el("span", { className: "station-update__feature" }, ["📈 Live Sparklines"]),
              el("span", { className: "station-update__feature" }, ["🎯 Tier-Based Hierarchy"])
            ]),
            el("a", { href: "#/signals", className: "btn-primary station-update__cta" }, [
              "Enter the Signals Terminal →"
            ])
          ])
        ])
      ])
    ]),

    // Live Stress Board - Now with real data
    el("section", { className: "section reveal-section", id: "signals" }, [
      el("div", { className: "section-header" }, [
        el("h2", {}, ["Live Stress Board"]),
        el("p", { className: "text-faint" }, ["Key indicators across liquidity, rates, credit & volatility"]),
        el("a", { href: "#/signals", className: "section-link" }, ["View All 120+ Signals →"])
      ]),
      el("div", { className: "stress-board-container" }, [
        el("div", { className: "stress-grid stress-grid--scrollable", id: "live-stress-grid" }, [])
      ])
    ]),

    // Observatory Network Teaser
    el("section", { className: "section observatory-network-teaser section-transition" }, [
      el("div", { className: "observatory-teaser-bg" }, []),
      el("div", { className: "observatory-teaser-content" }, [
        el("div", { className: "observatory-teaser-text" }, [
          el("p", { className: "observatory-teaser-label" }, ["Enter"]),
          el("h2", { className: "observatory-teaser-title" }, ["The Observatory Network"]),
          el("p", { className: "observatory-teaser-description" }, [
            "Six specialized dashboards monitoring the fault lines of global finance. From dollar liquidity to EM carry risk — see stress building before it breaks."
          ]),
          el("div", { className: "observatory-teaser-meta" }, [
            el("span", { className: "btn-disabled" }, ["Coming Q1 2026"]),
          ])
        ]),
        el("div", { className: "observatory-teaser-stats" }, [
          el("div", { className: "teaser-stat" }, [
            el("span", { className: "teaser-stat-value" }, ["6"]),
            el("span", { className: "teaser-stat-label" }, ["Observatories"])
          ]),
          el("div", { className: "teaser-stat" }, [
            el("span", { className: "teaser-stat-value" }, ["0"]),
            el("span", { className: "teaser-stat-label" }, ["Live Now"])
          ]),
          el("div", { className: "teaser-stat" }, [
            el("span", { className: "teaser-stat-value" }, ["24/7"]),
            el("span", { className: "teaser-stat-label" }, ["Monitoring"])
          ]),
          el("div", { className: "teaser-stat" }, [
            el("span", { className: "teaser-stat-value" }, ["∞"]),
            el("span", { className: "teaser-stat-label" }, ["Doom Potential"])
          ])
        ])
      ])
    ]),

    // Dispatches from the Station Teaser
    el("section", { className: "section dispatches-teaser section-transition" }, [
      el("div", { className: "dispatches-teaser-bg" }, []),
      el("div", { className: "dispatches-teaser-overlay" }, []),
      el("div", { className: "dispatches-teaser-content dispatches-teaser-content--minimal" }, [
        el("div", { className: "dispatches-teaser-text" }, [
          el("p", { className: "dispatches-teaser-label" }, ["FROM THE CRYPT"]),
          el("h2", { className: "dispatches-teaser-title" }, ["Dispatches from the Station"]),
          el("p", { className: "dispatches-teaser-description" }, [
            "Analysis, commentary, and signals from the macro watchtower. Where dead narratives are buried and uncomfortable truths are exhumed."
          ]),
          el("a", { href: "#/dispatches", className: "btn-primary dispatches-teaser-cta" }, [
            "Enter the Dispatches →"
          ])
        ])
      ])
    ]),

    // Capitulation Station (Forum) Teaser
    el("section", { className: "section capitulation-station-teaser section-transition" }, [
      el("div", { className: "station-teaser-bg" }, []),
      el("div", { className: "station-teaser-content" }, [
        el("div", { className: "station-teaser-text" }, [
          el("h2", { className: "station-title" }, ["Capitulation Station"]),
          el("p", { className: "station-tagline" }, ["Home of the Capitulators"]),
          el("p", { className: "station-description" }, [
            "A message board for the doom-pilled macro community. No algorithms, no engagement farming — just threads, charts, and the collective wisdom of people who've seen this movie before."
          ]),
          el("div", { className: "station-meta" }, [
            el("span", { className: "coming-pill" }, ["Coming Q1 2026"]),
            el("a", { href: "#/forum", className: "btn-ghost station-btn" }, ["Learn More →"])
          ])
        ]),
        el("div", { className: "station-features" }, [
          el("div", { className: "feature-item" }, [
            el("span", { className: "feature-icon" }, ["📜"]),
            el("span", {}, ["Long-form threads"])
          ]),
          el("div", { className: "feature-item" }, [
            el("span", { className: "feature-icon" }, ["📊"]),
            el("span", {}, ["Chart sharing"])
          ]),
          el("div", { className: "feature-item" }, [
            el("span", { className: "feature-icon" }, ["🎭"]),
            el("span", {}, ["House sigils"])
          ]),
          el("div", { className: "feature-item" }, [
            el("span", { className: "feature-icon" }, ["🔔"]),
            el("span", {}, ["Stress alerts"])
          ])
        ])
      ])
    ])
  ]);
}

/**
 * Populates the home page with data after DOM insertion
 */
export async function initHomePage() {
  // Show loading skeletons immediately
  const stressGrid = document.getElementById("live-stress-grid");
  if (stressGrid) {
    // Add skeletons while loading
    for (let i = 0; i < FEATURED_SIGNALS.length; i++) {
      stressGrid.appendChild(createStressCardSkeleton());
    }
    
    // Fetch real signal data
    try {
      console.log('[Home] Fetching live signal data...');
      const response = await preloadAllSignals();
      
      if (response.success && response.signals) {
        // Clear skeletons
        stressGrid.innerHTML = '';
        
        // Flatten signals from all categories
        const allSignals = Object.values(response.signals).flat();
        
        // Find featured signals and create cards
        let cardsAdded = 0;
        for (const signalId of FEATURED_SIGNALS) {
          const signal = allSignals.find(s => s.signal_id === signalId);
          if (signal && signal.current) {
            stressGrid.appendChild(createLiveStressCard(signal));
            cardsAdded++;
          }
        }
        
        console.log(`[Home] Added ${cardsAdded} live signal cards`);
        
        // If no signals loaded, show error state
        if (cardsAdded === 0) {
          stressGrid.innerHTML = '';
          stressGrid.appendChild(
            el("div", { className: "stress-board-error" }, [
              el("p", {}, ["Unable to load signal data"]),
              el("a", { href: "#/signals", className: "btn-ghost" }, ["Try Signals Page →"])
            ])
          );
        }
      } else {
        throw new Error('Failed to fetch signals');
      }
    } catch (error) {
      console.error('[Home] Error loading signals:', error);
      stressGrid.innerHTML = '';
      stressGrid.appendChild(
        el("div", { className: "stress-board-error" }, [
          el("p", {}, ["Signal feed temporarily unavailable"]),
          el("a", { href: "#/signals", className: "btn-ghost" }, ["Try Signals Page →"])
        ])
      );
    }
  }

  // Setup scroll reveal
  setupScrollReveal();
}

/**
 * Sets up scroll reveal animations
 */
function setupScrollReveal() {
  const revealSections = document.querySelectorAll(".reveal-section");
  if (!revealSections.length) return;

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReducedMotion) {
    revealSections.forEach(section => section.classList.add("revealed"));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add("revealed");
      }
    });
  }, {
    root: null,
    rootMargin: "0px 0px -100px 0px",
    threshold: 0.1
  });

  revealSections.forEach(section => observer.observe(section));
}

export { initHomePage as initHomePageEffects };
